import React, { useState, useEffect, useMemo } from 'react';
import {
  Panel, Badge, Button, Modal, Input, Select,
  Table, TableHead, TableHeader, TableBody, TableRow, TableCell,
} from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { Spinner } from '../ui';
import { Investment, PortfolioSummary, FinancialAccount } from '../../types/finance';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const TYPE_GROUPS: { key: string; label: string; types: string[] }[] = [
  { key: 'stocks_etfs', label: 'Stocks & ETFs', types: ['stock', 'etf', 'mutual_fund', 'bond'] },
  { key: 'crypto', label: 'Crypto', types: ['crypto'] },
  { key: 'real_estate', label: 'Real Estate', types: ['real_estate'] },
  { key: 'other', label: 'Other', types: ['other'] },
];

const INVESTMENT_TYPES = [
  { value: 'stock', label: 'Stock' },
  { value: 'etf', label: 'ETF' },
  { value: 'mutual_fund', label: 'Mutual Fund' },
  { value: 'bond', label: 'Bond' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  name: '',
  ticker_symbol: '',
  investment_type: 'stock',
  quantity: '',
  purchase_price: '',
  purchase_date: new Date().toISOString().split('T')[0],
  account: '',
  crypto_id: '',
  address: '',
  notes: '',
};

const HoldingsTable: React.FC<{ investments: Investment[] }> = ({ investments }) => (
  <Panel style={{ padding: 0, overflow: 'hidden' }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Name</TableHeader>
          <TableHeader>Ticker</TableHeader>
          <TableHeader align="right">Qty</TableHeader>
          <TableHeader align="right">Cost Basis</TableHeader>
          <TableHeader align="right">Value</TableHeader>
          <TableHeader align="right">Gain/Loss</TableHeader>
          <TableHeader align="right">%</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {investments.map(inv => (
          <TableRow key={inv.id}>
            <TableCell>
              <span style={{ fontWeight: '500', color: COLORS.text.primary }}>{inv.name}</span>
            </TableCell>
            <TableCell>
              <span style={{ color: COLORS.text.secondary }}>{inv.ticker_symbol || '-'}</span>
            </TableCell>
            <TableCell align="right">{Number(inv.quantity).toFixed(2)}</TableCell>
            <TableCell align="right">{formatCurrency(inv.total_cost)}</TableCell>
            <TableCell align="right">
              <span style={{ fontWeight: '500' }}>{formatCurrency(inv.current_value)}</span>
            </TableCell>
            <TableCell align="right">
              <span style={{
                color: inv.gain_loss >= 0 ? COLORS.status.success : COLORS.status.error,
                fontWeight: '500',
              }}>
                {formatCurrency(inv.gain_loss)}
              </span>
            </TableCell>
            <TableCell align="right">
              <span style={{
                color: inv.gain_loss_percentage >= 0 ? COLORS.status.success : COLORS.status.error,
                fontWeight: '500',
              }}>
                {inv.gain_loss_percentage >= 0 ? '+' : ''}{inv.gain_loss_percentage.toFixed(2)}%
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Panel>
);

const InvestmentPortfolio: React.FC = () => {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [investmentAccounts, setInvestmentAccounts] = useState<FinancialAccount[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, sumRes, acctRes] = await Promise.all([
        apiClient.get('/api/investments/?is_active=true'),
        apiClient.get('/api/investments/portfolio_summary/'),
        apiClient.get('/api/financial-accounts/?is_active=true'),
      ]);
      setInvestments(invRes.data.results || invRes.data);
      setSummary(sumRes.data);
      const allAccts: FinancialAccount[] = acctRes.data.results || acctRes.data;
      setInvestmentAccounts(allAccts.filter(a => ['investment', 'brokerage', 'retirement'].includes(a.account_type)));
    } catch (err) {
      console.error('Failed to fetch investments', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await apiClient.post('/api/investments/refresh_prices/');
      await fetchData();
    } catch (err) {
      console.error('Failed to refresh prices', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddOpen = async () => {
    setForm(EMPTY_FORM);
    setShowAddModal(true);
    try {
      const res = await apiClient.get('/api/financial-accounts/?is_active=true');
      setAccounts(res.data.results || res.data);
    } catch (err) {
      console.error('Failed to fetch accounts', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post('/api/investments/', {
        ...form,
        quantity: parseFloat(form.quantity) || 0,
        purchase_price: parseFloat(form.purchase_price) || 0,
        account: parseInt(form.account) || undefined,
      });
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      console.error('Failed to create investment', err);
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const map: Record<string, Investment[]> = {};
    for (const g of TYPE_GROUPS) {
      map[g.key] = investments.filter(i => g.types.includes(i.investment_type));
    }
    return map;
  }, [investments]);

  const toggleGroup = (key: string) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const gainLossPct = summary?.gain_loss_percentage ?? 0;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[32] }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          {summary?.last_updated && (
            <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
              Prices updated {new Date(summary.last_updated).toLocaleString()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh Prices'}
          </Button>
          <Button variant="primary" onClick={handleAddOpen}>
            Add Investment
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACING[12] }}>
          <Panel>
            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Total Value</div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontFamily: TYPOGRAPHY.headings.fontFamily, fontWeight: '700', color: COLORS.text.primary }}>
              {formatCurrency(summary.total_value)}
            </div>
          </Panel>
          <Panel>
            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Total Cost</div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontFamily: TYPOGRAPHY.headings.fontFamily, fontWeight: '700', color: COLORS.text.primary }}>
              {formatCurrency(summary.total_cost)}
            </div>
          </Panel>
          <Panel>
            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Gain/Loss</div>
            <div style={{
              fontSize: TYPOGRAPHY.headings.sizes.h2,
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontWeight: '700',
              color: summary.gain_loss >= 0 ? COLORS.status.success : COLORS.status.error,
            }}>
              {formatCurrency(summary.gain_loss)}
            </div>
          </Panel>
          <Panel>
            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Return %</div>
            <div style={{
              fontSize: TYPOGRAPHY.headings.sizes.h2,
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontWeight: '700',
              color: gainLossPct >= 0 ? COLORS.status.success : COLORS.status.error,
            }}>
              {gainLossPct >= 0 ? '+' : ''}{gainLossPct.toFixed(2)}%
            </div>
          </Panel>
        </div>
      )}

      {/* Type breakdown badges */}
      {summary?.by_type && Object.keys(summary.by_type).length > 0 && (
        <div style={{ display: 'flex', gap: SPACING[8], flexWrap: 'wrap' }}>
          {Object.entries(summary.by_type).map(([type, data]) => (
            <Badge key={type} variant="default">
              {type.replace('_', ' ')}: {data.count} ({formatCurrency(data.value)})
            </Badge>
          ))}
        </div>
      )}

      {/* Investment Accounts */}
      {investmentAccounts.length > 0 && (
        <Panel style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${SPACING[8]} ${SPACING[12]}`,
            backgroundColor: COLORS.bg.hover,
            borderBottom: `1px solid ${COLORS.border.default}`,
          }}>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
            }}>
              Investment Accounts
            </div>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: '700',
              color: COLORS.text.primary,
            }}>
              {formatCurrency(investmentAccounts.reduce((sum, a) => sum + a.current_balance, 0))}
            </div>
          </div>
          {investmentAccounts.map(a => (
            <div key={a.id} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${SPACING[8]} ${SPACING[12]}`,
              borderBottom: `1px solid ${COLORS.border.default}`,
              fontSize: TYPOGRAPHY.body.sizes.default,
            }}>
              <div>
                <div style={{ color: COLORS.text.primary, fontWeight: '500' }}>{a.name}</div>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
                  {a.institution_name || a.account_type_display}
                </div>
              </div>
              <div style={{ fontWeight: '600', color: COLORS.text.primary }}>
                {formatCurrency(a.current_balance)}
              </div>
            </div>
          ))}
        </Panel>
      )}

      {/* Grouped holdings */}
      {investments.length === 0 && investmentAccounts.length === 0 ? (
        <Panel style={{ textAlign: 'center', padding: SPACING[32] }}>
          <p style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.secondary, margin: 0 }}>
            No active investments found.
          </p>
        </Panel>
      ) : (
        TYPE_GROUPS.map(g => {
          const items = grouped[g.key];
          if (!items || items.length === 0) return null;
          const groupValue = items.reduce((sum, i) => sum + i.current_value, 0);
          const isCollapsed = collapsed[g.key];

          return (
            <div key={g.key}>
              <div
                onClick={() => toggleGroup(g.key)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: `${SPACING[8]} ${SPACING[4]}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <div style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                }}>
                  {isCollapsed ? '\u25B6' : '\u25BC'} {g.label}
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, marginLeft: SPACING[8] }}>
                    ({items.length})
                  </span>
                </div>
                <div style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  fontWeight: '700',
                  color: COLORS.text.primary,
                }}>
                  {formatCurrency(groupValue)}
                </div>
              </div>
              {!isCollapsed && <HoldingsTable investments={items} />}
            </div>
          );
        })
      )}

      {/* Add Investment Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Investment" footer={
        <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          <div>
            <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Name</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Apple Inc." />
          </div>
          <div>
            <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Type</label>
            <Select value={form.investment_type} onChange={e => setForm(f => ({ ...f, investment_type: e.target.value }))} options={INVESTMENT_TYPES} />
          </div>
          <div>
            <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
              {form.investment_type === 'crypto' ? 'Ticker / Symbol' : 'Ticker Symbol'}
            </label>
            <Input value={form.ticker_symbol} onChange={e => setForm(f => ({ ...f, ticker_symbol: e.target.value }))} placeholder={form.investment_type === 'crypto' ? 'e.g. BTC' : 'e.g. AAPL'} />
          </div>
          {form.investment_type === 'crypto' && (
            <div>
              <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>CoinGecko ID (optional)</label>
              <Input value={form.crypto_id} onChange={e => setForm(f => ({ ...f, crypto_id: e.target.value }))} placeholder="e.g. bitcoin, ethereum" />
            </div>
          )}
          {form.investment_type === 'real_estate' && (
            <div>
              <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Address (for Zillow lookup)</label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="e.g. 123 Main St, City, ST 12345" />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Quantity</label>
              <Input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" />
            </div>
            <div>
              <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Purchase Price</label>
              <Input type="number" value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="0.00" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Purchase Date</label>
            <Input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>Account</label>
            <Select value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))}>
              <option value="">Select account...</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvestmentPortfolio;
